<template>
  <aside
    id="app-sidebar-9"
    class="kb-print-hide kb-rail kb-screen-h hidden lg:flex flex-col flex-shrink-0 absolute lg:static left-0 top-0 z-30 bg-kb-pane border-r border-kb-line transition-[width] duration-200"
    :class="isCollapsed ? 'lg:w-[52px]' : 'w-[232px] lg:w-[216px]'"
  >
    <div
      class="h-[52px] flex-shrink-0 flex items-center gap-2.5 border-b border-kb-line"
      :class="showLabels ? 'px-3.5' : 'justify-center'"
    >
      <NuxtLink
        to="/"
        class="flex items-center gap-2.5 min-w-0"
        aria-label="Home"
        @click="closeDrawer()"
      >
        <img
          src="~/assets/images/logo.svg"
          alt=""
          class="h-[30px] w-auto flex-shrink-0 [filter:var(--kb-logo-filter)]"
        />
        <span
          v-if="showLabels"
          class="kb-wordmark text-[15px] truncate"
        >krewebacchus</span>
      </NuxtLink>
    </div>

    <nav
      class="kb-autoscrollbar flex-1 min-h-0 overflow-y-auto px-2 py-2.5 flex flex-col gap-px"
      aria-label="Main"
    >
      <template
        v-for="section in sections"
        :key="section.key"
      >
        <div
          v-if="section.title"
          class="kb-label px-2.5 pt-4 pb-1.5"
          :class="showLabels ? '' : 'text-center !px-0'"
        >
          {{ showLabels ? section.title : '·' }}
        </div>

        <template
          v-for="entry in section.items"
          :key="entry.label"
        >
          <!-- Group with sub-pages -->
          <template v-if="isGroup(entry)">
            <a
              v-tooltip.right="!showLabels ? entry.label : null"
              class="kb-nav-item"
              :class="[groupActive(entry) ? 'is-active' : '', showLabels ? '' : 'justify-center']"
              role="button"
              :aria-expanded="showLabels ? isExpanded(entry) : undefined"
              @click="showLabels ? toggleGroup(entry) : go(firstChildTo(entry))"
            >
              <i :class="['pi', entry.icon]" />
              <template v-if="showLabels">
                <span class="flex-1 truncate">{{ entry.label }}</span>
                <i
                  class="pi pi-chevron-down !text-[10px] text-kb-dim transition-transform"
                  :class="isExpanded(entry) ? '' : '-rotate-90'"
                />
              </template>
            </a>
            <div
              v-if="showLabels && isExpanded(entry)"
              class="kb-nav-children"
            >
              <a
                v-for="child in entry.children"
                :key="child.label"
                class="kb-nav-item kb-nav-child"
                :class="isActive(child) ? 'is-active' : ''"
                :href="child.href"
                :target="child.href ? '_blank' : undefined"
                :rel="child.href ? 'noopener' : undefined"
                @click="child.to ? go(child.to) : undefined"
              >
                <span class="flex-1 min-w-0">{{ child.label }}</span>
                <i
                  v-if="child.href"
                  class="pi pi-external-link !text-[10px] text-kb-dim"
                />
              </a>
            </div>
          </template>

          <!-- Single link -->
          <a
            v-else
            v-tooltip.right="!showLabels ? entry.label : null"
            class="kb-nav-item"
            :class="[isActive(entry) ? 'is-active' : '', showLabels ? '' : 'justify-center']"
            :href="entry.href"
            :target="entry.href ? '_blank' : undefined"
            :rel="entry.href ? 'noopener' : undefined"
            @click="entry.to ? go(entry.to) : undefined"
          >
            <i :class="['pi', entry.icon]" />
            <span
              v-if="showLabels"
              class="flex-1 truncate"
            >{{ entry.label }}</span>
            <i
              v-if="showLabels && entry.href"
              class="pi pi-external-link !text-[10px] text-kb-dim"
            />
          </a>
        </template>
      </template>
    </nav>

    <div class="flex-shrink-0 border-t border-kb-line px-2 py-2 flex flex-col gap-px">
      <div class="lg:hidden flex flex-col gap-px">
        <a
          class="kb-nav-item"
          :class="isActiveRoute('/notifications') ? 'is-active' : ''"
          @click="go('/notifications')"
        >
          <span class="relative">
            <i class="pi pi-bell" />
            <span
              v-if="unreadCount > 0"
              class="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-kb-gold"
              aria-label="Unread notifications"
            />
          </span>
          <span class="flex-1">Notifications</span>
        </a>
      </div>
      <a
        v-tooltip.right="!showLabels ? 'Support' : null"
        class="kb-nav-item"
        :class="[isActiveRoute('/support') ? 'is-active' : '', showLabels ? '' : 'justify-center']"
        @click="go('/support')"
      >
        <i class="pi pi-question-circle" />
        <span
          v-if="showLabels"
          class="flex-1"
        >Support</span>
      </a>
      <div
        v-if="showLabels"
        class="kb-nav-item kb-nav-static"
      >
        <i class="pi pi-palette" />
        <span class="flex-1">Theme</span>
        <KbThemeSwitch />
      </div>
      <KbThemeSwitch
        v-else
        single
        class="kb-nav-item justify-center"
      />
      <div class="lg:hidden flex flex-col gap-px">
        <a
          class="kb-nav-item"
          @click="handleLogout"
        >
          <i class="pi pi-sign-out" />
          <span class="flex-1">Sign out</span>
        </a>
      </div>
      <div class="hidden lg:flex flex-col">
        <a
          v-tooltip.right="!showLabels ? 'Expand sidebar' : null"
          class="kb-nav-item"
          :class="showLabels ? '' : 'justify-center'"
          role="button"
          :aria-label="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
          @click="toggleCollapse"
        >
          <i :class="['pi', isCollapsed ? 'pi-angle-double-right' : 'pi-angle-double-left']" />
          <span
            v-if="showLabels"
            class="flex-1"
          >Collapse</span>
        </a>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDrawer } from '~/composables/useDrawer'
import { useNotifications } from '~/composables/useNotifications'

const props = defineProps({
  isAdmin: { type: Boolean, default: false },
  isBandAdmin: { type: Boolean, default: false },
  isRedshirtAdmin: { type: Boolean, default: false },
  isRedshirtLieutenant: { type: Boolean, default: false },
  isCheckinVolunteer: { type: Boolean, default: false },
  isCaptain: { type: Boolean, default: false },
  hasSubkrewe: { type: Boolean, default: false },
})

interface NavLink {
  label: string
  icon?: string
  to?: string
  href?: string
}

interface NavGroup {
  label: string
  icon: string
  children: NavLink[]
}

interface NavSection {
  key: string
  title?: string
  items: (NavLink | NavGroup)[]
}

const route = useRoute()
const router = useRouter()
const { unreadCount } = useNotifications()
const { closeDrawer } = useDrawer()

const isDesktop = ref(false)
const isCollapsed = ref(false)
const expanded = ref<Record<string, boolean>>({})

const showLabels = computed(() => !isCollapsed.value || !isDesktop.value)

const adminOnly = computed(() => props.isRedshirtAdmin && !props.isAdmin)
const bandOnly = computed(() => props.isBandAdmin && !props.isAdmin)
const canManageLineup = computed(() => props.isAdmin || props.isRedshirtAdmin)
const canRunLineup = computed(() => props.isAdmin || props.isRedshirtAdmin || props.isRedshirtLieutenant)

const sections = computed<NavSection[]>(() => {
  const main: (NavLink | NavGroup)[] = [
    { label: 'Home', icon: 'pi-home', to: '/' },
    { label: props.hasSubkrewe ? 'My SubKrewe' : 'Find SubKrewe', icon: props.hasSubkrewe ? 'pi-flag' : 'pi-search', to: '/subkrewe' },
    { label: 'My QR code', icon: 'pi-qrcode', to: '/checkin/qrcode' },
    { label: 'My orders', icon: 'pi-receipt', to: '/purchases' },
    { label: 'Settings', icon: 'pi-cog', to: '/settings' },
  ]
  // Everyone can view the published lineup; staff get it as a group below
  if (!canRunLineup.value) main.push({ label: 'Lineup', icon: 'pi-map-marker', to: '/lineup' })
  if (props.isAdmin || props.isRedshirtAdmin || props.isCaptain) {
    main.push({ label: 'Forum', icon: 'pi-comments', href: 'https://dis.chewbacchus.org' })
  }

  const admin: (NavLink | NavGroup)[] = []
  if (props.isAdmin || props.isRedshirtAdmin) {
    admin.push({ label: adminOnly.value ? 'Red Shirts' : 'Members', icon: 'pi-users', to: '/admin/members' })
  }
  if (adminOnly.value) admin.push({ label: 'Captains', icon: 'pi-star', to: '/admin/captains' })
  if (props.isAdmin || props.isBandAdmin || props.isRedshirtAdmin) {
    admin.push({ label: bandOnly.value ? 'Bands' : 'SubKrewes', icon: 'pi-sitemap', to: '/admin/subkrewes' })
  }
  if (props.isAdmin || props.isRedshirtAdmin) {
    admin.push({ label: 'Inventory', icon: 'pi-box', children: [
      { label: 'Management', to: '/admin/inventory' },
      { label: 'Equipment station', to: '/checkin/equipment' },
    ] })
  }
  if (canRunLineup.value) {
    const lineup: NavLink[] = []
    if (canManageLineup.value) lineup.push({ label: 'Manage', to: '/admin/lineup' })
    lineup.push({ label: 'View', to: '/lineup' })
    lineup.push({ label: 'Check in', to: '/admin/lineup-status' })
    lineup.push({ label: 'Radio list', to: '/admin/radio-list' })
    admin.push({ label: 'Lineup', icon: 'pi-map-marker', children: lineup })
  }
  if (props.isAdmin) {
    admin.push(
      { label: 'Reporting', icon: 'pi-chart-bar', children: [
        { label: 'Emails', to: '/admin/emails' },
        { label: 'Lifetimers', to: '/admin/reporting/lifetimers' },
        { label: 'VIP passes', to: '/admin/reporting/vip-passes' },
        { label: 'Checked in', to: '/admin/reporting/checked-in' },
        { label: 'SubKrewe membership', to: '/admin/reporting/subkrewe-membership' },
        { label: 'Bulk discounts', to: '/admin/reporting/bulk-discounts' },
        { label: 'Member discounts', to: '/admin/reporting/member-discounts' },
        { label: 'Analytics', href: 'https://lookerstudio.google.com/s/gpYIkrk-SJU' },
      ] },
      { label: 'Sales', icon: 'pi-dollar', children: [
        { label: 'Products & pricing', to: '/admin/products' },
        { label: 'Transactions', to: '/admin/transactions' },
      ] },
      { label: 'Tracking', icon: 'pi-map', to: '/tracking' },
      { label: 'System settings', icon: 'pi-sliders-h', to: '/admin/settings' },
    )
  }

  const result: NavSection[] = [{ key: 'main', items: main }]
  if (admin.length) result.push({ key: 'admin', title: 'Admin', items: admin })
  if (props.isCheckinVolunteer) {
    result.push({ key: 'parade', title: 'Parade day', items: [
      { label: 'Check people in', icon: 'pi-sign-in', to: '/checkin' },
    ] })
  }
  return result
})

function isGroup(entry: NavLink | NavGroup): entry is NavGroup {
  return 'children' in entry
}

const isActiveRoute = (path: string) => route.path === path
const isActive = (link: NavLink) => !!link.to && isActiveRoute(link.to)
const groupActive = (group: NavGroup) => group.children.some(isActive)
const isExpanded = (group: NavGroup) => expanded.value[group.label] ?? false
const firstChildTo = (group: NavGroup) => group.children.find(c => c.to)?.to ?? '/'

function toggleGroup(group: NavGroup) {
  expanded.value = { ...expanded.value, [group.label]: !isExpanded(group) }
}

// Open the group that holds the current page
watch([() => route.path, sections], () => {
  for (const section of sections.value) {
    for (const entry of section.items) {
      if (isGroup(entry) && groupActive(entry)) expanded.value = { ...expanded.value, [entry.label]: true }
    }
  }
}, { immediate: true })

function go(path: string) {
  closeDrawer()
  router.push(path)
}

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value
  try {
    localStorage.setItem('sidebarCollapsed', isCollapsed.value ? 'true' : 'false')
  }
  catch {
    // storage unavailable; the preference just won't persist
  }
}

async function handleLogout() {
  const { signOutUser } = useAuth()
  await signOutUser()
}

const checkIfDesktop = () => {
  isDesktop.value = window.innerWidth >= 1024
}

onMounted(() => {
  checkIfDesktop()
  window.addEventListener('resize', checkIfDesktop)
  try {
    if (localStorage.getItem('sidebarCollapsed') === 'true') isCollapsed.value = true
  }
  catch {
    // storage unavailable
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', checkIfDesktop)
})
</script>

<style>
.kb-nav-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-height: 32px;
  padding: 0 0.625rem;
  border-radius: 3px;
  font-size: 13.5px;
  color: var(--kb-muted);
  cursor: pointer;
  text-decoration: none;
  user-select: none;
}

.kb-nav-item > .pi:first-child,
.kb-nav-item > span > .pi {
  font-size: 15px;
  width: 16px;
  text-align: center;
}

.kb-nav-item:hover {
  background: var(--kb-hover);
  color: var(--kb-fg);
}

/* A row that holds its own controls (the theme switch) instead of being one */
.kb-nav-static,
.kb-nav-static:hover {
  background: none;
  color: var(--kb-muted);
  cursor: default;
}

.kb-nav-item.is-active {
  background: var(--kb-hover);
  color: var(--kb-fg);
  box-shadow: inset 2px 0 0 var(--kb-accent);
}

.kb-nav-item.is-active > .pi:first-child {
  color: var(--kb-accent);
}

/* A group's links hang off a guide line under the group's icon. They wrap
   rather than truncate, and the open one is marked on the guide line (green
   segment, green text) instead of the filled row the top level uses. */
.kb-nav-children {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin: 1px 0 4px 18px;
  padding-left: 9px;
  border-left: 1px solid var(--kb-line);
}

.kb-nav-child {
  position: relative;
  min-height: 28px;
  padding: 0.25rem 0.375rem;
  font-size: 13px;
  line-height: 1.3;
}

.kb-nav-item.kb-nav-child.is-active {
  background: transparent;
  box-shadow: none;
  color: var(--kb-accent);
  font-weight: 600;
}

.kb-nav-item.kb-nav-child.is-active::before {
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: -11px;
  width: 3px;
  border-radius: 2px;
  background: var(--kb-accent);
  content: "";
}

.kb-nav-item.kb-nav-child.is-active:hover {
  background: var(--kb-hover);
}

/* Mobile: the rail opens as an overlay drawer from the tab bar */
@media (max-width: 1023px) {
  .kb-rail:not(.hidden) {
    display: flex;
    box-shadow: 0 0 60px var(--kb-shadow);
  }
}
</style>
